/* CAELVERNE DIAMONDS — storefront behaviour (vanilla replacement for the approved React/Radix UI).
   Every feature initialises per section root and returns a cleanup, so the theme editor can reload sections safely. */
(() => {
  "use strict";
  const C = (window.Caelverne = window.Caelverne || {});
  const reduceQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const saveData = () => !!(navigator.connection && navigator.connection.saveData);
  const motionAllowed = () => !reduceQuery.matches && !saveData();

  /* ---------------- API client (no secrets; the service revalidates everything) ---------------- */
  class ServiceDisabled extends Error {}
  C.ServiceDisabled = ServiceDisabled;
  C.api = async (route, init = {}) => {
    if (!C.enquiriesEnabled) throw new ServiceDisabled(C.disabledMessage || "Online enquiries are not connected yet.");
    const base = String(C.apiBase || "/apps/caelverne").replace(/\/$/, "");
    const res = await fetch(`${base}/${route}`, { credentials: "same-origin", ...init, headers: { Accept: "application/json", ...(init.headers || {}) } });
    let data = {};
    try { data = await res.json(); } catch { /* non-JSON (proxy error page) */ }
    return { res, data };
  };
  C.uuid = () => {
    if (crypto.randomUUID) return crypto.randomUUID();
    const b = crypto.getRandomValues(new Uint8Array(16)); b[6] = (b[6] & 15) | 64; b[8] = (b[8] & 63) | 128;
    const h = Array.from(b, x => x.toString(16).padStart(2, "0")).join("");
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  };

  /* ---------------- First-touch campaign attribution (this browser session only; no contact data) ---------------- */
  const ATTR_KEY = "caelverne.attribution";
  (() => {
    try {
      if (sessionStorage.getItem(ATTR_KEY)) return;
      const q = new URLSearchParams(location.search), a = {};
      ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach(k => { const v = q.get(k); if (v) a[k] = v.slice(0, 200); });
      a.landing_path = location.pathname.slice(0, 300);
      if (document.referrer) { const h = new URL(document.referrer).host; if (h && h !== location.host) a.referrer_host = h.slice(0, 200); }
      sessionStorage.setItem(ATTR_KEY, JSON.stringify(a));
    } catch { /* storage unavailable: attribution is optional */ }
  })();
  C.attribution = () => { try { return JSON.parse(sessionStorage.getItem(ATTR_KEY) || "null"); } catch { return null; } };
  C.sourceUrl = () => location.pathname.slice(0, 300);

  /* ---------------- Dialogs: focus trap, Escape, overlay close, focus return ---------------- */
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  let active = null;
  const dialogs = {
    open(name, opener) {
      const wrap = document.querySelector(`[data-cv-dialog="${name}"]`);
      if (!wrap) return null;
      if (active && active.wrap !== wrap) this.close(false);
      const content = wrap.querySelector('[role="dialog"]');
      active = { wrap, content, opener: opener || document.activeElement, name };
      wrap.hidden = false;
      document.documentElement.classList.add("cv-dialog-open");
      document.dispatchEvent(new CustomEvent("cv:dialog", { detail: { open: true, name } }));
      requestAnimationFrame(() => {
        const first = content.querySelector("[autofocus]") || content.querySelector(FOCUSABLE);
        (first || content).focus({ preventScroll: true });
      });
      return wrap;
    },
    close(restore = true) {
      if (!active) return;
      const { wrap, opener, name } = active;
      wrap.hidden = true;
      active = null;
      document.documentElement.classList.remove("cv-dialog-open");
      document.dispatchEvent(new CustomEvent("cv:dialog", { detail: { open: false, name } }));
      if (restore && opener && document.contains(opener)) opener.focus({ preventScroll: true });
    },
    get active() { return active; },
  };
  C.dialogs = dialogs;
  document.addEventListener("keydown", e => {
    if (!active) return;
    if (e.key === "Escape") { e.preventDefault(); dialogs.close(); return; }
    if (e.key !== "Tab") return;
    const items = [...active.content.querySelectorAll(FOCUSABLE)].filter(el => el.offsetParent !== null || el === document.activeElement);
    if (!items.length) { e.preventDefault(); return; }
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === active.content)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
  document.addEventListener("click", e => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (t.closest("[data-cv-dialog-overlay]") || t.closest("[data-cv-dialog-close]")) { dialogs.close(); return; }
    const link = t.closest("[data-cv-dialog-close-link]");
    if (link) { dialogs.close(false); return; }
    const nav = t.closest("[data-cv-open-nav]");
    if (nav) { dialogs.open("nav", nav); return; }
    const quote = t.closest("[data-cv-open-quote]");
    if (quote) { e.preventDefault(); if (C.openQuote) C.openQuote(quote.getAttribute("data-inspiration") || "", quote); }
  });

  /* ---------------- Helpers ---------------- */
  const setToggle = (btn, paused) => {
    if (!btn) return;
    btn.setAttribute("aria-label", paused ? btn.dataset.labelPlay : btn.dataset.labelPause);
    btn.querySelector("[data-icon-pause]")?.toggleAttribute("hidden", paused);
    btn.querySelector("[data-icon-play]")?.toggleAttribute("hidden", !paused);
    const text = btn.querySelector("[data-text]");
    if (text) text.textContent = paused ? (btn.closest("[data-cv-film],[data-cv-lens]")?.dataset.playText || "Play") : (btn.closest("[data-cv-film],[data-cv-lens]")?.dataset.pauseText || "Pause");
  };

  /* ---------------- Features ---------------- */
  const features = {
    marquee(root) {
      const cleanups = [];
      root.querySelectorAll("[data-cv-marquee]").forEach(bar => {
        const btn = bar.querySelector("[data-cv-marquee-toggle]");
        const onClick = () => { const p = !bar.classList.contains("is-paused"); bar.classList.toggle("is-paused", p); setToggle(btn, p); };
        btn?.addEventListener("click", onClick);
        cleanups.push(() => btn?.removeEventListener("click", onClick));
      });
      return () => cleanups.forEach(f => f());
    },

    film(root) {
      const cleanups = [];
      root.querySelectorAll("[data-cv-film]").forEach(film => {
        const btn = film.querySelector("[data-cv-film-toggle]");
        const poster = film.querySelector(".hero-poster");
        let video = null, observer = null, visible = false, paused = false;
        const sync = () => { if (!video) return; if (visible && !paused && !document.hidden) video.play().catch(() => { paused = true; update(); }); else video.pause(); };
        const update = () => { film.classList.toggle("film-paused", paused || !video); setToggle(btn, paused); sync(); };
        const teardown = () => { observer?.disconnect(); observer = null; video?.remove(); video = null; if (btn) btn.hidden = true; film.classList.add("film-paused"); };
        const setup = () => {
          if (!motionAllowed() || video) { if (!motionAllowed()) teardown(); return; }
          video = document.createElement("video");
          Object.assign(video, { muted: true, loop: true, playsInline: true, preload: "metadata" });
          video.className = "hero-video"; video.setAttribute("aria-hidden", "true"); video.style.opacity = "0";
          video.poster = poster?.currentSrc || poster?.src || ""; video.src = film.dataset.src;
          video.addEventListener("loadeddata", () => { video && (video.style.opacity = "1"); }, { once: true });
          video.addEventListener("error", () => teardown(), { once: true });
          (poster || film.firstChild) ? film.insertBefore(video, poster ? poster.nextSibling : film.firstChild) : film.appendChild(video);
          if (btn) btn.hidden = false;
          observer = new IntersectionObserver(([en]) => { visible = en.isIntersecting; sync(); }, { threshold: .05 });
          observer.observe(video);
          update();
        };
        const onBtn = () => { paused = !paused; update(); };
        const onMotion = () => { if (reduceQuery.matches) { paused = true; teardown(); } else setup(); };
        btn?.addEventListener("click", onBtn);
        reduceQuery.addEventListener("change", onMotion);
        document.addEventListener("visibilitychange", sync);
        setup();
        cleanups.push(() => { btn?.removeEventListener("click", onBtn); reduceQuery.removeEventListener("change", onMotion); document.removeEventListener("visibilitychange", sync); teardown(); });
      });
      return () => cleanups.forEach(f => f());
    },

    lens(root) {
      const cleanups = [];
      root.querySelectorAll("[data-cv-lens]").forEach(study => {
        const lens = study.querySelector(".ring-lens"), glass = study.querySelector(".lens-glass"), btn = study.querySelector(".ring-study-toggle");
        let video = null, observer = null, visible = false, paused = false, activeZoom = false;
        const sync = () => { if (!video) return; video.style.animationPlayState = paused || activeZoom ? "paused" : "running"; if (visible && !paused && !activeZoom && !document.hidden) video.play().catch(() => { paused = true; setToggle(btn, true); }); else video.pause(); };
        const teardown = () => { observer?.disconnect(); video?.remove(); video = null; if (btn) btn.hidden = true; };
        const setup = () => {
          if (!motionAllowed() || video) { if (!motionAllowed()) teardown(); return; }
          video = document.createElement("video");
          Object.assign(video, { muted: true, loop: true, playsInline: true, preload: "none" });
          video.className = "ring-study-film"; video.setAttribute("aria-hidden", "true"); video.poster = study.dataset.poster; video.src = study.dataset.src;
          video.addEventListener("error", teardown, { once: true });
          lens.insertBefore(video, glass);
          if (btn) btn.hidden = false;
          observer = new IntersectionObserver(([en]) => { visible = en.isIntersecting; sync(); }, { threshold: .15 });
          observer.observe(video); sync();
        };
        const onClick = () => { activeZoom = !activeZoom; lens.classList.toggle("lens-active", activeZoom); lens.setAttribute("aria-pressed", String(activeZoom)); lens.setAttribute("aria-label", activeZoom ? lens.dataset.labelClose : lens.dataset.labelOpen); sync(); };
        const onMove = e => { if (e.pointerType !== "mouse") return; const r = lens.getBoundingClientRect(); const x = (e.clientX - r.left) / r.width * 100, y = (e.clientY - r.top) / r.height * 100; glass.style.left = `${x}%`; glass.style.top = `${y}%`; glass.style.backgroundPosition = `${x}% ${y}%`; };
        const onBtn = () => { paused = !paused; setToggle(btn, paused); sync(); };
        const onMotion = () => (reduceQuery.matches ? teardown() : setup());
        lens.addEventListener("click", onClick); lens.addEventListener("pointermove", onMove); btn?.addEventListener("click", onBtn);
        reduceQuery.addEventListener("change", onMotion); document.addEventListener("visibilitychange", sync);
        setup();
        cleanups.push(() => { lens.removeEventListener("click", onClick); lens.removeEventListener("pointermove", onMove); btn?.removeEventListener("click", onBtn); reduceQuery.removeEventListener("change", onMotion); document.removeEventListener("visibilitychange", sync); teardown(); });
      });
      return () => cleanups.forEach(f => f());
    },

    gallery(root) {
      const cleanups = [];
      root.querySelectorAll("[data-cv-gallery]").forEach(gallery => {
        const dataEl = gallery.querySelector("[data-cv-gallery-data]");
        let data = { rings: {} };
        try { data = JSON.parse(dataEl.textContent); } catch { /* keep defaults */ }
        const triggers = [...gallery.querySelectorAll('[role="tab"]')];
        const count = gallery.querySelector("[data-cv-count]");
        const collapseAll = () => gallery.querySelectorAll("[data-cv-gallery-toggle]").forEach(t => setExpanded(t, false));
        const setExpanded = (t, open) => {
          const panel = document.getElementById(t.getAttribute("aria-controls"));
          t.setAttribute("aria-expanded", String(open));
          t.firstChild.nodeValue = open ? t.dataset.less : t.dataset.more;
          t.querySelector("[data-sign]").textContent = open ? "−" : "+";
          panel.classList.toggle("is-expanded", open); panel.setAttribute("aria-hidden", String(!open)); panel.toggleAttribute("inert", !open);
        };
        const select = (tab, focus) => {
          triggers.forEach(t => {
            const on = t === tab;
            t.setAttribute("aria-selected", String(on)); t.dataset.state = on ? "active" : "inactive"; t.tabIndex = on ? 0 : -1;
            const panel = document.getElementById(t.getAttribute("aria-controls"));
            panel.hidden = !on; panel.dataset.state = on ? "active" : "inactive";
          });
          if (count) count.textContent = `${tab.dataset.count} ${count.dataset.suffix}`;
          collapseAll();
          if (focus) tab.focus();
        };
        const onTabClick = e => { const t = e.target.closest('[role="tab"]'); if (t) select(t, false); };
        const onTabKey = e => {
          const i = triggers.indexOf(document.activeElement); if (i < 0) return;
          const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: triggers.length - 1 }[e.key];
          if (next === undefined) return;
          e.preventDefault(); select(triggers[(next + triggers.length) % triggers.length], true);
        };
        const list = gallery.querySelector('[role="tablist"]');
        list?.addEventListener("click", onTabClick); list?.addEventListener("keydown", onTabKey);
        const onToggle = e => { const t = e.target.closest("[data-cv-gallery-toggle]"); if (t) setExpanded(t, t.getAttribute("aria-expanded") !== "true"); };
        gallery.addEventListener("click", onToggle);

        // Ring cards: hover shows the side view; swipe/arrow keys rotate angles; click opens the detail dialog.
        gallery.querySelectorAll("[data-cv-ring]").forEach(card => {
          const imgs = [...card.querySelectorAll(".gallery-angle")], dots = [...card.querySelectorAll(".angle-dots i")];
          let angle = 0, hovered = false, start = null, swiped = false;
          const show = () => { const shown = hovered ? 1 : angle; imgs.forEach((img, i) => { const on = i === shown; img.classList.toggle("is-visible", on); if (on) img.removeAttribute("aria-hidden"); else img.setAttribute("aria-hidden", "true"); if (on) img.loading = "eager"; }); dots.forEach((d, i) => d.classList.toggle("active", i === shown)); };
          const turn = dir => { hovered = false; angle = (angle + (dir > 0 ? 1 : 2)) % 3; show(); };
          const h = {
            pointerenter: e => { if (e.pointerType === "mouse") { hovered = true; show(); } },
            pointerleave: () => { hovered = false; show(); },
            pointerdown: e => { start = { x: e.clientX, y: e.clientY }; swiped = false; },
            pointercancel: () => { start = null; },
            pointerup: e => { if (!start) return; const dx = e.clientX - start.x, dy = e.clientY - start.y; start = null; if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) { swiped = true; turn(dx < 0 ? 1 : -1); } },
            keydown: e => { if (e.key === "ArrowRight" || e.key === "ArrowLeft") { e.preventDefault(); turn(e.key === "ArrowRight" ? 1 : -1); } },
            click: () => { if (swiped) { swiped = false; return; } openRing(data, card.dataset.cvRing, card); },
          };
          Object.entries(h).forEach(([k, f]) => card.addEventListener(k, f));
          show();
          cleanups.push(() => Object.entries(h).forEach(([k, f]) => card.removeEventListener(k, f)));
        });
        cleanups.push(() => { list?.removeEventListener("click", onTabClick); list?.removeEventListener("keydown", onTabKey); gallery.removeEventListener("click", onToggle); });
      });
      return () => cleanups.forEach(f => f());
    },

    compare(root) {
      const cleanups = [];
      root.querySelectorAll("[data-cv-compare]").forEach(el => {
        const input = el.querySelector('input[type="range"]'), study = el.querySelector(".compare-study"), divider = el.querySelector(".compare-divider");
        const onInput = () => { const v = Number(input.value); study.style.clipPath = `inset(0 ${100 - v}% 0 0)`; divider.style.left = `${v}%`; };
        input.addEventListener("input", onInput); onInput();
        cleanups.push(() => input.removeEventListener("input", onInput));
      });
      return () => cleanups.forEach(f => f());
    },

    valuePanels(root) {
      const observers = [];
      root.querySelectorAll("[data-cv-value-panel]").forEach(panel => {
        // Each panel is observed independently so both animate on narrow screens.
        if (!("IntersectionObserver" in window)) { panel.classList.add("has-entered"); return; }
        const o = new IntersectionObserver(([en]) => { if (en.isIntersecting) { panel.classList.add("has-entered"); o.disconnect(); } }, { threshold: .15 });
        o.observe(panel); observers.push(o);
      });
      return () => observers.forEach(o => o.disconnect());
    },

    carousel(root) {
      const cleanups = [];
      root.querySelectorAll("[data-cv-carousel]").forEach(car => {
        const track = car.querySelector("[data-cv-track]"), slides = [...track.children], prev = car.querySelector("[data-cv-prev]"), next = car.querySelector("[data-cv-next]");
        const viewport = track.parentElement;
        let index = 0, drag = null;
        const maxIndex = () => { const vw = viewport.clientWidth; let total = 0; slides.forEach(s => total += s.offsetWidth); const per = slides[0]?.offsetWidth || vw; return Math.max(0, Math.ceil((total - vw - 1) / per)); };
        const go = i => {
          index = Math.max(0, Math.min(i, maxIndex()));
          const offset = slides[index] ? slides[index].offsetLeft - slides[0].offsetLeft : 0;
          track.style.transition = reduceQuery.matches ? "none" : "transform .45s cubic-bezier(.22,.61,.36,1)";
          track.style.transform = `translate3d(${-offset}px, 0px, 0px)`;
          prev.disabled = index === 0; next.disabled = index >= maxIndex();
          slides.forEach((s, i) => s.setAttribute("aria-hidden", String(i < index || s.offsetLeft - slides[0].offsetLeft - (slides[index]?.offsetLeft - slides[0].offsetLeft || 0) >= viewport.clientWidth - 2)));
        };
        const h = {
          prev: () => go(index - 1), next: () => go(index + 1),
          key: e => { if (e.target !== car) return; if (e.key === "ArrowLeft") { e.preventDefault(); go(index - 1); } if (e.key === "ArrowRight") { e.preventDefault(); go(index + 1); } },
          down: e => { if (e.pointerType === "mouse") return; drag = { x: e.clientX, y: e.clientY }; },
          up: e => { if (!drag) return; const dx = e.clientX - drag.x, dy = e.clientY - drag.y; drag = null; if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) go(index + (dx < 0 ? 1 : -1)); },
          resize: () => go(index),
        };
        prev.addEventListener("click", h.prev); next.addEventListener("click", h.next); car.addEventListener("keydown", h.key);
        viewport.addEventListener("pointerdown", h.down); viewport.addEventListener("pointerup", h.up); window.addEventListener("resize", h.resize);
        go(0);
        cleanups.push(() => { prev.removeEventListener("click", h.prev); next.removeEventListener("click", h.next); car.removeEventListener("keydown", h.key); viewport.removeEventListener("pointerdown", h.down); viewport.removeEventListener("pointerup", h.up); window.removeEventListener("resize", h.resize); });
      });
      return () => cleanups.forEach(f => f());
    },

    accordion(root) {
      const cleanups = [];
      root.querySelectorAll("[data-cv-accordion]").forEach(acc => {
        const items = [...acc.querySelectorAll('[data-slot="accordion-item"]')];
        const set = (item, open) => {
          const trig = item.querySelector('[data-slot="accordion-trigger"]'), content = item.querySelector('[data-slot="accordion-content"]');
          const state = open ? "open" : "closed";
          [item, trig, content, trig.parentElement].forEach(el => (el.dataset.state = state));
          trig.setAttribute("aria-expanded", String(open));
          if (open) { content.hidden = false; content.style.setProperty("--radix-accordion-content-height", `${content.scrollHeight}px`); }
          else if (!content.hidden) {
            content.style.setProperty("--radix-accordion-content-height", `${content.scrollHeight}px`);
            if (reduceQuery.matches) content.hidden = true; else setTimeout(() => { if (content.dataset.state === "closed") content.hidden = true; }, 200);
          }
        };
        const onClick = e => { const trig = e.target.closest('[data-slot="accordion-trigger"]'); if (!trig) return; const item = trig.closest('[data-slot="accordion-item"]'); const open = item.dataset.state !== "open"; items.forEach(i => { if (i !== item && i.dataset.state === "open") set(i, false); }); set(item, open); };
        const onKey = e => {
          const trigs = items.map(i => i.querySelector('[data-slot="accordion-trigger"]')); const i = trigs.indexOf(document.activeElement); if (i < 0) return;
          const n = { ArrowDown: i + 1, ArrowUp: i - 1, Home: 0, End: trigs.length - 1 }[e.key]; if (n === undefined) return;
          e.preventDefault(); trigs[(n + trigs.length) % trigs.length].focus();
        };
        acc.addEventListener("click", onClick); acc.addEventListener("keydown", onKey);
        cleanups.push(() => { acc.removeEventListener("click", onClick); acc.removeEventListener("keydown", onKey); });
      });
      return () => cleanups.forEach(f => f());
    },

    reveals(root) {
      document.documentElement.classList.add("js-reveals");
      const els = root.querySelectorAll(".reveal, .signature-line");
      if (!("IntersectionObserver" in window)) { els.forEach(e => e.classList.add("is-visible")); return () => {}; }
      const o = new IntersectionObserver(entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add("is-visible"); o.unobserve(e.target); } }), { threshold: .06 });
      els.forEach(el => o.observe(el));
      return () => o.disconnect();
    },
  };

  /* ---------------- Ring detail dialog ---------------- */
  function openRing(data, blockId, opener) {
    const ring = data.rings && data.rings[blockId]; if (!ring) return;
    const wrap = document.querySelector('[data-cv-dialog="ring"]'); if (!wrap) return;
    const views = ["Front", "Side", "Three-quarter"];
    const main = wrap.querySelector("[data-ring-main]"), thumbs = [...wrap.querySelectorAll("[data-ring-thumb]")];
    const pick = i => { main.src = ring.images[i]; main.alt = `${views[i]} view of ${ring.name}`; thumbs.forEach((t, j) => t.setAttribute("aria-pressed", String(i === j))); };
    thumbs.forEach((t, i) => { t.querySelector("img").src = ring.images[i]; t.onclick = () => pick(i); });
    pick(0);
    wrap.querySelector("[data-ring-eyebrow]").textContent = data.eyebrow;
    wrap.querySelector("[data-ring-name]").textContent = ring.name;
    wrap.querySelector("[data-ring-note]").textContent = ring.note;
    wrap.querySelector("[data-ring-shape]").textContent = ring.shape;
    wrap.querySelector("[data-ring-metal]").textContent = ring.metal;
    wrap.querySelector("[data-ring-setting]").textContent = ring.setting;
    wrap.querySelector("[data-ring-cta-label]").textContent = data.cta;
    wrap.querySelector("[data-ring-disclaimer]").textContent = data.note;
    // Only the specifications the study actually shows travel to the builder; it revalidates each value.
    const params = new URLSearchParams({ inspiration: ring.id, name: ring.name });
    Object.entries(ring.preset || {}).forEach(([k, v]) => { if (v) params.set(k, v); });
    wrap.querySelector("[data-ring-cta]").href = `${data.designUrl}?${params}`;
    dialogs.open("ring", opener);
  }

  /* ---------------- Mobile action bar (after the hero, hidden while a dialog is open) ---------------- */
  function mobileBar() {
    const bar = document.querySelector("[data-cv-mobile-bar]"), hero = document.querySelector(".proposal-hero");
    if (!bar) return () => {};
    let past = !hero;
    const sync = () => { bar.hidden = !past || !!dialogs.active; };
    const o = hero ? new IntersectionObserver(([en]) => { past = !en.isIntersecting && en.boundingClientRect.bottom < 0; sync(); }) : null;
    if (o) o.observe(hero);
    document.addEventListener("cv:dialog", sync); sync();
    return () => { o?.disconnect(); document.removeEventListener("cv:dialog", sync); };
  }

  /* ---------------- Lifecycle (incl. theme editor) ---------------- */
  const registry = new WeakMap();
  const init = root => { const cleanups = Object.values(features).map(f => f(root)); registry.set(root, cleanups); };
  const destroy = root => { (registry.get(root) || []).forEach(f => f && f()); registry.delete(root); };
  let barCleanup = null;
  const boot = () => {
    document.querySelectorAll(".shopify-section").forEach(init);
    if (!document.querySelector(".shopify-section")) init(document.body);
    barCleanup = mobileBar();
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot); else boot();
  document.addEventListener("shopify:section:load", e => { init(e.target); barCleanup?.(); barCleanup = mobileBar(); });
  document.addEventListener("shopify:section:unload", e => destroy(e.target));
  document.addEventListener("shopify:block:select", e => {
    // Reveal a selected accordion answer or hidden gallery design while editing.
    const item = e.target.closest('[data-slot="accordion-item"]');
    if (item && item.dataset.state !== "open") item.querySelector('[data-slot="accordion-trigger"]')?.click();
    const hidden = e.target.closest(".gallery-reveal");
    if (hidden && !hidden.classList.contains("is-expanded")) hidden.parentElement.querySelector("[data-cv-gallery-toggle]")?.click();
  });
})();
